/**
 * In-app journal: connection lifecycle, SignalR hub interactions, trade ops.
 * Bounded ring buffer; not a substitute for server logs.
 */

import { create } from "zustand";
import { sanitizeForJournal } from "../sanitizeForJournal";

export type JournalLevel = "info" | "error" | "warning";
export type JournalDirection = "in" | "out" | "system";

export interface AppJournalEntry {
  id: string;
  timestamp: number;
  level: JournalLevel;
  category: string;
  message: string;
  /** Trimmed payload text only (max {@link JOURNAL_DETAIL_MAX_CHARS}). */
  detail?: string;
  messageType?: string;
  direction?: JournalDirection;
}

export const JOURNAL_MAX_ENTRIES = 250;

/** Max stored detail length; full hub payloads live in the raw message store. */
export const JOURNAL_DETAIL_MAX_CHARS = 250;

function nextId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function compactJournalDetail(data: unknown): string {
  const sanitized = sanitizeForJournal(data);
  let text: string;
  try {
    text =
      typeof sanitized === "string" ? sanitized : JSON.stringify(sanitized);
  } catch {
    text = String(sanitized);
  }
  if (text.length <= JOURNAL_DETAIL_MAX_CHARS) return text;
  return `${text.slice(0, JOURNAL_DETAIL_MAX_CHARS)}…`;
}

function normalizeDetail(
  detail: unknown | undefined,
  sanitizeDetail: boolean
): string | undefined {
  if (detail === undefined) return undefined;
  const value = sanitizeDetail ? sanitizeForJournal(detail) : detail;
  return compactJournalDetail(value);
}

interface JournalState {
  entries: AppJournalEntry[];
  addEntry: (entry: {
    level: JournalLevel;
    category: string;
    message: string;
    detail?: unknown;
    messageType?: string;
    direction?: JournalDirection;
    sanitizeDetail?: boolean;
  }) => void;
  clear: () => void;
}

export const useJournalStore = create<JournalState>((set) => ({
  entries: [],

  addEntry: ({
    level,
    category,
    message,
    detail,
    messageType,
    direction,
    sanitizeDetail = true,
  }) =>
    set((state) => {
      const row: AppJournalEntry = {
        id: nextId(),
        timestamp: Date.now(),
        level,
        category,
        message,
        detail: normalizeDetail(detail, sanitizeDetail),
        messageType,
        direction,
      };
      let next = [...state.entries, row];
      if (next.length > JOURNAL_MAX_ENTRIES) {
        next = next.slice(-JOURNAL_MAX_ENTRIES);
      }
      return { entries: next };
    }),

  clear: () => set({ entries: [] }),
}));

/** High-volume hub methods excluded from automatic incoming journal lines */
const SKIP_INCOMING_JOURNAL = new Set([
  "VybeOnQuote",
  "VybeOnOHLCData",
  "VybeOnTickHistory",
  "VybeOnMarketDepth",
  "VybeOnAccountUpdate",
]);

export function journalSignalRIncoming(method: string, args: unknown[]): void {
  if (SKIP_INCOMING_JOURNAL.has(method)) return;
  let summary = "";
  if (method === "VybeOnOrderHistory" && args[0]) {
    const payload = Array.isArray(args[0]) ? args[0][0] : args[0];
    if (payload && typeof payload === "object" && "success" in payload) {
      const p = payload as Record<string, unknown>;
      summary = ` success=${p.success}`;
      if (p.historySource) summary += ` source=${p.historySource}`;
      if (Array.isArray(p.orders)) summary += ` orders=${p.orders.length}`;
      if (Array.isArray(p.internals)) {
        summary += ` internals=${p.internals.length}`;
      }
      if (Array.isArray(p.deals)) summary += ` deals=${p.deals.length}`;
    }
    useJournalStore.getState().addEntry({
      level:
        (payload as { success?: boolean })?.success === false
          ? "warning"
          : "info",
      category: "signalr.in",
      message: `${method}${summary}`,
      detail: args,
      messageType: method,
      direction: "in",
      sanitizeDetail: false,
    });
    return;
  }

  if (method === "VybeOnSyncEconomicCalendarBatch" && args[0]) {
    const payload = (Array.isArray(args[0]) ? args[0][0] : args[0]) as Record<
      string,
      unknown
    >;
    const isLastBatch = Boolean(payload.isLastBatch ?? payload.IsLastBatch);
    if (!isLastBatch) return;

    const replaceScope = String(payload.replaceScope ?? payload.ReplaceScope ?? "");
    const monthVersionsRaw = payload.monthVersions ?? payload.MonthVersions;
    const monthVersionCount = Array.isArray(monthVersionsRaw)
      ? monthVersionsRaw.length
      : 0;

    // A hash-sync cycle replays one scope per stale MONTH — logging each one flooded the whole
    // 250-line journal with "done batch=…" the moment a few dozen months re-pulled. Only the
    // cycle's terminal batch carries the monthVersions payload, so that is the one line worth
    // keeping; non-month scopes (first load, day pulls) still log individually.
    if (replaceScope === "month" && monthVersionCount === 0) return;

    const batchIndex = Number(payload.batchIndex ?? payload.BatchIndex ?? 0);
    const totalBatches = Number(payload.totalBatches ?? payload.TotalBatches ?? 0);
    const scopeKey = String(payload.scopeKey ?? payload.ScopeKey ?? "");
    const events = payload.events ?? payload.Events;
    const eventCount = Array.isArray(events) ? events.length : 0;
    const scopeSuffix =
      replaceScope && scopeKey ? ` scope=${replaceScope}:${scopeKey}` : "";
    const versionSuffix =
      monthVersionCount > 0 ? ` monthsSynced=${monthVersionCount}` : "";
    useJournalStore.getState().addEntry({
      level: "info",
      category: "signalr.in",
      message: `${method} done batch=${batchIndex + 1}/${Math.max(totalBatches, 1)} events=${eventCount}${scopeSuffix}${versionSuffix}`,
      messageType: method,
      direction: "in",
    });
    return;
  }

  useJournalStore.getState().addEntry({
    level: "info",
    category: "signalr.in",
    message: method,
    detail: args,
    messageType: method,
    direction: "in",
    sanitizeDetail: false,
  });
}

export function journalSignalROutgoing(method: string, args: unknown[]): void {
  if (method === "SyncEconomicCalendar" && args[0]) {
    const payload = (Array.isArray(args[0]) ? args[0][0] : args[0]) as Record<
      string,
      unknown
    >;
    const monthVersions = payload.monthVersions ?? payload.MonthVersions;
    const monthCount = Array.isArray(monthVersions) ? monthVersions.length : 0;
    const years = payload.calendarYears ?? payload.CalendarYears;
    const yearCount = Array.isArray(years) ? years.length : 0;
    useJournalStore.getState().addEntry({
      level: "info",
      category: "signalr.out",
      message: `${method} years=${yearCount} monthHashes=${monthCount}`,
      messageType: method,
      direction: "out",
    });
    return;
  }

  useJournalStore.getState().addEntry({
    level: "info",
    category: "signalr.out",
    message: method,
    detail: args,
    messageType: method,
    direction: "out",
    sanitizeDetail: false,
  });
}
